import React, { useEffect, useState } from "react";

import validator from "validator";

import { Box, Field, Grid, GridItem, Icon, Input, Text, MetreaTooltip } from "@metrea-ui/v2";
import { NumericFormatAdapter } from "../common/NumericFormatAdapter";

import NewOption from "./NewOption";
import classes from "./AddValueToSelectedOption.module.css";
import { AddCircleRegular, DismissRegular } from "@fluentui/react-icons";

import { mColor } from "src/utils/themeUtils";
import { Session } from "src/utils/sessionUtils";
import { getRandomInteger, getValuesByPropertyPath, findObjectByProperty } from "src/utils/dataUtils";
import { debugLog } from "src/utils/debugUtils";

const AddValueToSelectedOption = (props) => {
	const [dataError, setDataError] = useState("");
	const [addNew, setAddNew] = useState(false);
	const [currSelected, setCurrSelected] = useState(props.value);

	const isValidMetricValue = (value) =>
		Boolean(value && value.trim() && validator.isInt(value.trim(), { min: 1 }));
	const safeSelected = Array.isArray(currSelected) ? currSelected : [];
	const hasInvalidMetricValues = safeSelected.some(
		(item) => !isValidMetricValue(String(item?.comp_value ?? "")),
	);
	const sectionInvalid = dataError !== "" || hasInvalidMetricValues;

	// Parent saves/reloads can replace the force-structure array under us, so keep
	// the local editor state and section-level validation aligned with props.value.
	useEffect(() => {
		const nextSelected = Array.isArray(props.value) ? props.value : [];
		setCurrSelected(nextSelected);
		setDataError(
			nextSelected.some((item) => !isValidMetricValue(String(item?.comp_value ?? "")))
				? "Values must be whole numbers greater than 0"
				: "",
		);
	}, [props.value]);

	// Keep this helper local while the force-structure grid still owns its
	// numeric validation and delete affordance behavior.
	const renderNumericField = ({
		id,
		fsId,
		label,
		value,
		onChange,
		disabled = false,
		invalid = false,
		endAdornment,
	}) => {
		return (
			<Field.Root invalid={invalid} style={{ width: "100%" }}>
				<Field.Label htmlFor={id}>{label}</Field.Label>
				<div style={{ position: "relative", width: "100%" }}>
					<NumericFormatAdapter
						customInput={Input}
						id={id}
						name={id}
						data-fs-id={fsId}
						value={value ?? ""}
						disabled={disabled}
						size="sm"
						variant="subtle"
						aria-invalid={invalid ? "true" : undefined}
						onChange={onChange}
						formatSpec={{
							kind: "number",
							decimals: 0,
							fixed: false,
							allowNegative: false,
						}}
						style={{
							width: "100%",
							paddingRight: endAdornment ? 38 : undefined,
							fontSize: "0.8rem",
						}}
					/>
					{endAdornment ? (
						<span
							style={{
								position: "absolute",
								right: 8,
								top: "50%",
								transform: "translateY(-50%)",
								display: "inline-flex",
								alignItems: "center",
								pointerEvents: "auto",
							}}
						>
							{endAdornment}
						</span>
					) : null}
				</div>
			</Field.Root>
		);
	};

	const handleInputChange = (event) => {
		const { name, value } = event.target;

		// NumericFormatAdapter forwards a minimal synthetic event, so derive the item id
		// from the stable field name instead of relying on DOM dataset propagation.
		const fsIdMatch = /^force-structure-(\d+)$/.exec(String(name ?? ""));
		const fsId = fsIdMatch ? Number(fsIdMatch[1]) : NaN;

		const thisItem = findObjectByProperty(currSelected, "id", fsId);
		if (!thisItem) {
			debugLog(3, "[AddValueToSelectedOption][ACTION] handleInputChange.missingItem", {
				name,
				value,
				fsId,
				currSelected,
			});
			return;
		}

		thisItem.comp_value = value;
		thisItem.update_flag = thisItem.update_flag !== 101 ? 102 : 101;

		setDataError(isValidMetricValue(value) ? "" : "Values must be whole numbers greater than 0");

		setCurrSelected(currSelected);
		props.onUpdate(currSelected);
	};

	const deleteHandler = (item) => {
		const newSelected = currSelected.filter((x) => {
			return x.id !== item.id;
		});

		setCurrSelected(newSelected);
		props.onUpdate(newSelected, item);
	};

	const addNewHandler = (item) => {
		const thisItem =
			item && typeof item === "object"
				? item
				: (props.options || []).find((option) => String(option?.id) === String(item)) || null;

		if (!thisItem) {
			debugLog(3, "[AddValueToSelectedOption][ACTION] addNewHandler.invalidItem", {
				item: item,
				options: props.options,
			});
			return;
		}

		const newItem = {
			id: getRandomInteger(1000000000, 2000000000),
			baseline_id: props.currItem.baseline_id,
			item_id: props.currItem.id,
			comp_type_id: thisItem.id,
			comp_value: props.defaultValue,
			sort_id: 1,
			status_id: 1,
			update_flag: 101,
			label: thisItem.comp_value,
			color: thisItem.color,
			createdAt: new Date(),
			createdBy: Session().currUser.id,
			updatedAt: "",
			updatedBy: Session().currUser.id,
		};

		const newSelected = [...currSelected, newItem];

		setCurrSelected(newSelected);
		props.onUpdate(newSelected);
	};

	const showNewOptions = (item) => {
		debugLog(3, "[AddValueToSelectedOption][ACTION] showNewOptions", {
			item: item,
		});
		setAddNew(true);
	};

	const cancelHandler = (item) => {
		setAddNew(false);
	};

	return (
		<div className={`${classes.card} ${props.className}`}>
			<Box as="form" noValidate autoComplete="off" style={{ padding: "1rem" }}>
				<Box style={{ display: "flex", gap: 2 }}>
					<Text textStyle="md">{props.label}</Text>
					{props.mode === "edit" && !addNew && (
						<MetreaTooltip
							content={`Add New ${props.label}`}
							positioning={{ placement: "right" }}
						>
							<Icon as={AddCircleRegular} boxSize="24px" onClick={showNewOptions} />
						</MetreaTooltip>
					)}
				</Box>

				{addNew && (
					<Box>
						<NewOption
							mode={props.mode}
							size={props.size || "small"}
							width={props.width}
							item={props.options}
							selected={getValuesByPropertyPath(currSelected, "comp_type_id")}
							onAddNew={addNewHandler}
							onCancel={cancelHandler}
						/>
					</Box>
				)}
				<Box>
					{" "}
					{sectionInvalid && (
						<div
							style={{
								paddingLeft: "10px",
								paddingBottom: "10px",
								color: mColor.Danger,
								fontSize: "0.8rem",
							}}
						>
							{dataError || "Values must be whole numbers greater than 0"}
						</div>
					)}
				</Box>

				<Grid templateColumns="repeat(3, 1fr)" gap="2">
					{safeSelected.length > 0 &&
						safeSelected.map((v, index) => {
							const deleteIcon =
								props.mode === "edit" ? (
									<MetreaTooltip
										content="Delete this item"
									>
										<Icon
											as={DismissRegular}
											boxSize="24px"
											onClick={() => deleteHandler(v)}
										/>
									</MetreaTooltip>
								) : (
									<></>
								);
							return (
								<GridItem key={index}>
									{renderNumericField({
										id: `force-structure-${v.id}`,
										fsId: v.id,
										label: v.label,
										value: v.comp_value,
										onChange: handleInputChange,
										disabled: props.mode !== "edit",
										invalid: !isValidMetricValue(String(v.comp_value ?? "")),
										endAdornment: deleteIcon,
									})}
								</GridItem>
							);
						})}
					{safeSelected.length === 0 && (
						<>
							<Grid rowSpacing={1}>
								<Box mt={50} />
							</Grid>
						</>
					)}
				</Grid>
			</Box>
		</div>
	);
};

export default AddValueToSelectedOption;
